/**
 * Billing tasks API (GDN-2644) — the human billing work queue.
 *
 * Hand-written endpoints (same pattern as the v2 worklist endpoints in
 * api.ts) so this feature typechecks without regenerating the OpenAPI
 * client against a backend that already serves the new routes. Swap to
 * the generated `billing-v2` client once the backend endpoints are
 * deployed, if desired.
 */
import { baseApi } from '@store/baseApi';

export type BillingTaskStatus = 'open' | 'in_progress' | 'resolved' | 'dismissed';
export type BillingTaskCategory = 'manual_review' | 'era_attention' | 'withheld_posting';
export type BillingTaskSubjectType = 'claim' | 'remittance_claim';
export type PrincipalKind = 'user' | 'staff' | 'agent' | 'system' | 'patient';

export interface BillingTaskItem {
  billing_task_id: string;
  subject_type: BillingTaskSubjectType;
  subject_id: string;
  category: BillingTaskCategory;
  category_label: string;
  reason: string;
  status: BillingTaskStatus;
  status_label: string;
  created_by_principal_id: string;
  created_by_principal_kind: PrincipalKind;
  resolved_by_principal_id?: string | null;
  resolved_by_principal_kind?: PrincipalKind | null;
  resolved_at?: string | null;
  resolution_note?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BillingTaskSegment {
  key: string;
  label: string;
  count: number;
  severity?: string | null;
}

export interface BillingTaskListResponse {
  items: BillingTaskItem[];
  segments: BillingTaskSegment[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface BillingTaskResponse {
  task: BillingTaskItem;
}

export interface BillingTasksQueryArgs {
  status?: BillingTaskStatus;
  category?: BillingTaskCategory;
  subjectType?: BillingTaskSubjectType;
  subjectId?: string;
  page?: number;
  perPage?: number;
}

export interface CreateBillingTaskArgs {
  subject_type: BillingTaskSubjectType;
  subject_id: string;
  category: BillingTaskCategory;
  reason: string;
}

export interface UpdateBillingTaskStatusArgs {
  billingTaskId: string;
  status: BillingTaskStatus;
  resolutionNote?: string;
}

const TASKS_TAG = 'BillingV2Tasks' as const;

function compactParams<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
  ) as Partial<T>;
}

const tasksApi = baseApi.enhanceEndpoints({ addTagTypes: [TASKS_TAG] }).injectEndpoints({
  endpoints: build => ({
    getBillingTasks: build.query<BillingTaskListResponse, BillingTasksQueryArgs | undefined>({
      query: args => ({
        url: '/v2/provider/billing/v2/tasks',
        params: compactParams({
          status: args?.status,
          category: args?.category,
          subject_type: args?.subjectType,
          subject_id: args?.subjectId,
          page: args?.page,
          per_page: args?.perPage,
        }),
      }),
      providesTags: [TASKS_TAG],
    }),
    createBillingTask: build.mutation<BillingTaskResponse, CreateBillingTaskArgs>({
      query: body => ({
        url: '/v2/provider/billing/v2/tasks',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TASKS_TAG],
    }),
    updateBillingTaskStatus: build.mutation<BillingTaskResponse, UpdateBillingTaskStatusArgs>({
      query: ({ billingTaskId, status, resolutionNote }) => ({
        url: `/v2/provider/billing/v2/tasks/${billingTaskId}/status`,
        method: 'PATCH',
        body: compactParams({ status, resolution_note: resolutionNote }),
      }),
      invalidatesTags: [TASKS_TAG],
    }),
  }),
});

export const {
  useGetBillingTasksQuery: useBillingTasksQuery,
  useCreateBillingTaskMutation,
  useUpdateBillingTaskStatusMutation,
} = tasksApi;

export { tasksApi };
